import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Trash2, Upload, ChevronDown, ChevronUp, RefreshCw, Copy } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Textarea,
  Switch,
  Skeleton,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SegmentedControl,
  Disclosure,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useTLSConfig,
  useUpdateTLSConfig,
  useDeleteTLSConfig,
  useCertList,
  useUploadCert,
  useDeleteCert,
  useActivateCert,
  useIssueCert,
  useDistributeTigerCertToCdn,
  type TLSConfig,
  type CertDetail,
} from '@/api/manticore';
import { configTone, configLabel, formatDate, formatDateTime, toneColor } from '../shared';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import {
  useCerts,
  useIssueCert as useIssueTigerCert,
  useRecheckCert,
  type Cert,
  type CertStatus,
} from '@/api/tiger';
import { TigerCertPicker } from '@/pages/services/components/TigerCertPicker';
import styles from './SslTab.module.css';

// Tiger cert statuses that are still resolving (poll while any is present).
const TRANSIENT_TIGER: ReadonlySet<CertStatus> = new Set<CertStatus>(['awaiting_dns', 'issuing', 'pending']);

// ── TLS form ────────────────────────────────────────────────────────────
interface TlsForm {
  minVersion: string;
  http2: boolean;
  http3: boolean;
  autoHttps: boolean;
  hstsEnabled: boolean;
  hstsMaxAge: string;
  hstsSubdomains: boolean;
  hstsPreload: boolean;
}

const TLS_DEFAULTS: TlsForm = {
  minVersion: '1.2',
  http2: true,
  http3: false,
  autoHttps: false,
  hstsEnabled: true,
  hstsMaxAge: '31536000',
  hstsSubdomains: true,
  hstsPreload: false,
};

function hydrateTls(t: TLSConfig): TlsForm {
  return {
    minVersion: t.min_version || '1.2',
    http2: t.http2_enabled ?? true,
    http3: t.http3_enabled ?? false,
    autoHttps: t.auto_https_redirect ?? false,
    hstsEnabled: t.hsts?.enabled ?? true,
    hstsMaxAge: String(t.hsts?.max_age_secs ?? 31536000),
    hstsSubdomains: t.hsts?.include_subdomains ?? true,
    hstsPreload: t.hsts?.preload ?? false,
  };
}

function toTlsPayload(v: TlsForm): TLSConfig {
  return {
    min_version: v.minVersion,
    http2_enabled: v.http2,
    http3_enabled: v.http3,
    auto_https_redirect: v.autoHttps,
    hsts: {
      enabled: v.hstsEnabled,
      max_age_secs: Number(v.hstsMaxAge) || 0,
      include_subdomains: v.hstsSubdomains,
      preload: v.hstsPreload,
    },
  };
}

export function SslTab({ domain, domainId }: { domain: string; domainId: string }) {
  return (
    <Section title="SSL / TLS" description={`HTTPS transport security for ${domain}.`}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <TlsConfigCard domainId={domainId} />
        <CertificatesCard domainId={domainId} />
        <TigerInventoryCard domain={domain} domainId={domainId} />
        <PendingTigerCerts domain={domain} />
      </div>
    </Section>
  );
}

// ── TLS / HTTPS ─────────────────────────────────────────────────────────
function TlsConfigCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: tls, isLoading } = useTLSConfig(domainId);
  const isConfigured = !!tls;

  const [form, setForm] = useState<TlsForm>(TLS_DEFAULTS);
  const [snapshot, setSnapshot] = useState<TlsForm>(TLS_DEFAULTS);
  useEffect(() => {
    const next = tls ? hydrateTls(tls) : TLS_DEFAULTS;
    setForm(next);
    setSnapshot(next);
  }, [tls]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(snapshot), [form, snapshot]);
  const set = <K extends keyof TlsForm>(k: K, v: TlsForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const update = useUpdateTLSConfig(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'tls', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'TLS configuration saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save TLS configuration', description: errorMessage(e, 'Could not save TLS configuration') }),
  });
  const remove = useDeleteTLSConfig(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'tls', 'delete');
      show({ tone: 'success', title: 'TLS configuration removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove TLS configuration', description: errorMessage(e, 'Could not remove TLS configuration') }),
  });
  const busy = update.isPending || remove.isPending;

  if (isLoading) return <Skeleton style={{ height: 240 }} />;

  return (
    <Card>
      <CardHeader actions={<StatusPill tone={configTone(isConfigured)}>{configLabel(isConfigured)}</StatusPill>}>
        <CardTitle>TLS / HTTPS</CardTitle>
        <CardDescription>Protocol versions and HTTP Strict Transport Security.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
        <Field label="Minimum TLS version" description="TLS 1.3 recommended; 1.2 needed for older clients.">
          {(props) => (
            <Select value={form.minVersion} onValueChange={(v) => set('minVersion', v)}>
              <SelectTrigger {...props} style={{ maxWidth: 280 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1.2">TLS 1.2</SelectItem>
                <SelectItem value="1.3">TLS 1.3</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>

        <SettingRow
          title="HTTP/2 & gRPC"
          description="Multiplex requests over one connection (required for gRPC)."
          control={<Switch checked={form.http2} onCheckedChange={(v) => set('http2', v)} />}
        />
        <SettingRow
          title="HTTP/3 (QUIC)"
          description="Faster connections on lossy networks. Requires HTTP/2."
          control={
            <Switch checked={form.http3} disabled={!form.http2} onCheckedChange={(v) => set('http3', v)} />
          }
        />
        <SettingRow
          title="Auto HTTPS redirect"
          description="Send a 301 for all plain-HTTP requests."
          control={<Switch checked={form.autoHttps} onCheckedChange={(v) => set('autoHttps', v)} />}
        />

        <SettingRow
          title="HSTS"
          description="Tell browsers to only ever connect over HTTPS."
          control={<Switch checked={form.hstsEnabled} onCheckedChange={(v) => set('hstsEnabled', v)} />}
        />
        {form.hstsEnabled && (
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', marginTop: 'var(--vn-space-3)' }}>
            <Field label="Max age (s)" description="How long browsers remember to use HTTPS. 31536000 (1 year) recommended.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  value={form.hstsMaxAge}
                  onChange={(e) => set('hstsMaxAge', e.target.value)}
                  style={{ maxWidth: 280 }}
                />
              )}
            </Field>
            <SettingRow
              title="Include subdomains"
              control={<Switch checked={form.hstsSubdomains} onCheckedChange={(v) => set('hstsSubdomains', v)} />}
            />
            <SettingRow
              title="Preload"
              description="Submit to the browser preload list. Requires include subdomains. Hard to undo."
              control={<Switch checked={form.hstsPreload} onCheckedChange={(v) => set('hstsPreload', v)} />}
            />
          </div>
        )}

        <ActionBar
          sticky={false}
          start={
            isConfigured ? (
              <Button variant="secondary" disabled={busy} onClick={() => remove.mutate()}>
                Remove
              </Button>
            ) : undefined
          }
          end={
            <>
              {isConfigured && (
                <Button variant="secondary" disabled={!dirty || busy} onClick={() => setForm(snapshot)}>
                  Cancel
                </Button>
              )}
              <Button disabled={(isConfigured && !dirty) || busy} onClick={() => update.mutate(toTlsPayload(form))}>
                {update.isPending ? 'Saving…' : isConfigured ? 'Save changes' : 'Save'}
              </Button>
            </>
          }
        />
      </CardBody>
    </Card>
  );
}

// ── Certificates ───────────────────────────────────────────────────────
function CertificatesCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: byProvider = {}, isLoading } = useCertList(domainId);
  const certs = useMemo(() => Object.values(byProvider).flat(), [byProvider]);

  const [provider, setProvider] = useState('zerossl');
  const [showUpload, setShowUpload] = useState(false);
  const [label, setLabel] = useState('');
  const [cert, setCert] = useState('');
  const [chain, setChain] = useState('');
  const [key, setKey] = useState('');
  const [activate, setActivate] = useState(true);

  const issue = useIssueCert(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cert', 'create');
      show({ tone: 'success', title: 'Certificate issuance started' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not start issuance', description: errorMessage(e, 'Could not start issuance') }),
  });
  const upload = useUploadCert(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cert', 'create');
      show({ tone: 'success', title: 'Certificate uploaded' });
      setShowUpload(false);
      setLabel('');
      setCert('');
      setChain('');
      setKey('');
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not upload certificate', description: errorMessage(e, 'Could not upload certificate') }),
  });

  const canUpload = cert.includes('-----BEGIN CERTIFICATE-----') && key.includes('PRIVATE KEY');

  return (
    <>
      <Card>
        <CardHeader
          actions={
            <Button variant="secondary" startIcon={<Upload size={15} />} onClick={() => setShowUpload((s) => !s)}>
              Upload certificate
            </Button>
          }
        >
          <CardTitle>SSL certificates</CardTitle>
          <CardDescription>Issue a free managed certificate or upload your own.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-4)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-muted)' }}>Issue free SSL</span>
            <SegmentedControl
              items={[
                { value: 'zerossl', label: 'ZeroSSL' },
                { value: 'letsencrypt', label: "Let's Encrypt" },
              ]}
              value={provider}
              onValueChange={setProvider}
              aria-label="Certificate provider"
            />
            <Button
              variant="secondary"
              startIcon={<ShieldCheck size={15} />}
              disabled={issue.isPending}
              onClick={() => issue.mutate({ provider })}
            >
              {issue.isPending ? 'Requesting…' : 'Issue free SSL'}
            </Button>
          </div>

          {showUpload && (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', borderTop: '1px solid var(--vn-border-default)', paddingTop: 'var(--vn-space-4)' }}>
              <Field label="Label" description="Optional name to identify this certificate.">
                {(props) => (
                  <Input {...props} value={label} placeholder="e.g. GlobalSign 2026" onChange={(e) => setLabel(e.target.value)} />
                )}
              </Field>
              <Field label="Certificate (leaf)" description="The server/leaf certificate only.">
                {(props) => (
                  <Textarea
                    {...props}
                    rows={4}
                    value={cert}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    onChange={(e) => setCert(e.target.value)}
                    style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)' }}
                  />
                )}
              </Field>
              <Field label="Certificate chain (intermediates)" description="Intermediate / root CA certs. Leave empty if not needed.">
                {(props) => (
                  <Textarea
                    {...props}
                    rows={4}
                    value={chain}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;... Intermediate CA ...&#10;-----END CERTIFICATE-----"
                    onChange={(e) => setChain(e.target.value)}
                    style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)' }}
                  />
                )}
              </Field>
              <Field label="Private key" description="PEM with a PRIVATE KEY marker.">
                {(props) => (
                  <Textarea
                    {...props}
                    rows={3}
                    value={key}
                    placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                    onChange={(e) => setKey(e.target.value)}
                    style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)' }}
                  />
                )}
              </Field>
              <SettingRow
                title="Activate immediately"
                description="Serve this certificate as soon as it is uploaded."
                control={<Switch checked={activate} onCheckedChange={setActivate} />}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
                <Button variant="secondary" onClick={() => setShowUpload(false)}>
                  Cancel
                </Button>
                <Button
                  startIcon={<Upload size={15} />}
                  disabled={!canUpload || upload.isPending}
                  onClick={() =>
                    upload.mutate({
                      cert_pem: cert,
                      key_pem: key,
                      chain_pem: chain || undefined,
                      label: label || undefined,
                      activate,
                    })
                  }
                >
                  {upload.isPending ? 'Uploading…' : 'Upload'}
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Installed certificates{certs.length ? ` (${certs.length})` : ''}</CardTitle>
          <CardDescription>Certificates currently available to serve HTTPS for this domain.</CardDescription>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Skeleton style={{ height: 80 }} />
          ) : certs.length === 0 ? (
            <EmptyState
              size="sm"
              icon={<ShieldCheck size={20} />}
              title="No certificates configured yet"
              description="The default server certificate will be used until you add one."
            />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              {certs.map((c) => (
                <CertRow key={c.id} cert={c} domainId={domainId} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function CertRow({ cert, domainId }: { cert: CertDetail; domainId: string }) {
  const { show } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteCert(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cert', 'delete');
      show({ tone: 'success', title: 'Certificate deleted' });
      setConfirmDelete(false);
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not delete certificate', description: errorMessage(e, 'Could not delete certificate') }),
  });
  const activate = useActivateCert(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cert', 'edit');
      show({ tone: 'success', title: 'Certificate activated' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not activate certificate', description: errorMessage(e, 'Could not activate certificate') }),
  });

  const valid = cert.not_after ? new Date(cert.not_after) > new Date() : true;
  const name = cert.label || `Cert #${cert.id}`;

  return (
    <div style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-3) var(--vn-space-4)' }}>
        <ShieldCheck size={18} color={cert.is_active ? toneColor('success') : 'var(--vn-fg-subtle)'} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-lg)' }}>{name}</span>
          {cert.is_active && <Badge tone="success" appearance="subtle" size="sm">Active</Badge>}
          <Badge tone={valid ? 'success' : 'danger'} appearance="outline" size="sm">
            {valid ? 'Valid' : 'Expired'}
          </Badge>
        </div>
        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {cert.not_after ? `Expires ${formatDate(cert.not_after)}` : ''}
        </span>
        {/* Serve a non-active installed cert. Active certs and expired ones have
            nothing to activate, so the action only shows for valid, inactive certs. */}
        {!cert.is_active && valid && (
          <Button
            variant="ghost"
            size="sm"
            startIcon={<ShieldCheck size={14} />}
            disabled={activate.isPending}
            onClick={() => activate.mutate(String(cert.id))}
          >
            {activate.isPending ? 'Activating…' : 'Activate'}
          </Button>
        )}
        <IconButton variant="ghost" size="sm" aria-label="Delete certificate" onClick={() => setConfirmDelete(true)}>
          <Trash2 size={15} />
        </IconButton>
        <IconButton variant="ghost" size="sm" aria-label="Toggle details" onClick={() => setExpanded((v) => !v)}>
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </IconButton>
      </div>
      {expanded && (
        <div style={{ borderTop: '1px solid var(--vn-border-default)', padding: 'var(--vn-space-4)' }}>
          <dl
            style={{
              display: 'grid',
              gap: 'var(--vn-space-3)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              margin: 0,
            }}
          >
            {(
              [
                ['Subject', cert.subject],
                ['Issuer', cert.issuer],
                ['Valid from', cert.not_before ? formatDate(cert.not_before) : '—'],
                ['Valid until', cert.not_after ? formatDate(cert.not_after) : '—'],
                ['Source', cert.source],
                ['Uploaded', cert.created_at ? formatDateTime(cert.created_at) : '—'],
              ] as [string, string | undefined][]
            ).map(([k, v]) => (
              <div key={k} style={{ display: 'grid', gap: 'var(--vn-space-0)' }}>
                <dt style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{k}</dt>
                <dd style={{ margin: 0, fontSize: 'var(--vn-font-size-md)' }}>{v || '—'}</dd>
              </div>
            ))}
          </dl>
          {cert.fingerprint_sha256 && (
            <div style={{ marginTop: 'var(--vn-space-3)' }}>
              <Disclosure summary="Fingerprint (SHA-256)">
                <code style={{ fontSize: 'var(--vn-font-size-sm)', wordBreak: 'break-all' }}>{cert.fingerprint_sha256}</code>
              </Disclosure>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete certificate?"
        description={`Delete certificate “${name}”? The default server certificate will be used if none remain.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate(String(cert.id))}
      />
    </div>
  );
}

// ── Managed SSL from Tiger inventory ────────────────────────────────────
/**
 * Reuse a certificate from the account's Tiger managed-SSL inventory on this CDN
 * domain — issued once, attachable across every service, private key stays
 * server-side. Mirrors the WAAP website SslTab: "Issue free SSL for this domain"
 * (Tiger issue → auto-distribute when it comes back active) + "Choose from
 * inventory" (picker → distribute). @mgmt fetches the bundle and installs the PEM
 * on the CDN edge; the installed-certs card above refreshes on success.
 */
function TigerInventoryCard({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);

  const distribute = useDistributeTigerCertToCdn(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cert', 'create');
      show({ tone: 'success', title: 'Certificate installed from inventory' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Install failed', description: errorMessage(e, 'Install failed') }),
  });

  // Issue a free Tiger cert for THIS domain inline. Owned-zone (shared wildcard)
  // comes back active → distribute here immediately; a custom domain lands
  // awaiting_dns and PendingTigerCerts surfaces the one-time CNAME.
  const issue = useIssueTigerCert({
    onSuccess: (c) => {
      if (c.status === 'active') {
        distribute.mutate({ cert_id: c.id });
      } else {
        show({
          tone: 'info',
          title: 'Certificate requested',
          description: 'Add the one-time DNS record shown below, then install it here.',
        });
      }
    },
    onError: (e) => show({ tone: 'danger', title: 'Issue failed', description: errorMessage(e, 'Issue failed') }),
  });
  const issueThis = () => domain && issue.mutate({ dns_names: [domain] });

  const busy = issue.isPending || distribute.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Managed SSL from your Tiger inventory</CardTitle>
        <CardDescription>
          Issue a free certificate for this domain, or reuse one you already have — issued once, attachable across
          every service. The private key stays server-side.
        </CardDescription>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            startIcon={<ShieldCheck size={15} />}
            disabled={!domain || busy}
            onClick={issueThis}
          >
            {issue.isPending ? 'Issuing…' : 'Issue free SSL for this domain'}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => setPickerOpen(true)}>
            {distribute.isPending ? 'Installing…' : 'Choose from inventory'}
          </Button>
        </div>
      </CardBody>

      <TigerCertPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        host={domain}
        onSelect={(cert) => distribute.mutate({ cert_id: cert.id })}
        onIssue={domain ? () => { setPickerOpen(false); issueThis(); } : undefined}
        issuing={issue.isPending}
        issueLabel={`Issue free SSL for ${domain}`}
      />
    </Card>
  );
}

/**
 * Surfaces any Tiger cert for THIS domain still resolving (a custom domain issued
 * above lands `awaiting_dns`). Shows the one-time delegation CNAME + a Recheck
 * button, and polls while transient so it flips to active — at which point the
 * user can install it via "Choose from inventory".
 */
function PendingTigerCerts({ domain }: { domain: string }) {
  const { show } = useToast();
  const { data: certs = [], refetch } = useCerts({ domain: domain || undefined, limit: 50 });

  const wildcard = domain ? `*.${domain}` : '';
  const pending = useMemo(
    () =>
      certs.filter(
        (c) => TRANSIENT_TIGER.has(c.status) && c.dns_names?.some((n) => n === domain || n === wildcard),
      ),
    [certs, domain, wildcard],
  );

  const hasTransient = pending.length > 0;
  useEffect(() => {
    if (!hasTransient) return;
    const t = setInterval(() => refetch(), 8000);
    return () => clearInterval(t);
  }, [hasTransient, refetch]);

  const recheck = useRecheckCert({
    onSuccess: () =>
      show({ tone: 'info', title: 'Checking…', description: 'Verifying the DNS record and issuing shortly.' }),
    onError: () => show({ tone: 'danger', title: 'Recheck failed' }),
  });

  if (!domain || pending.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending certificate verification</CardTitle>
        <CardDescription>
          A free certificate was requested for this domain but is still awaiting domain control. Add the one-time
          CNAME below at your DNS, then recheck.
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {pending.map((c: Cert) => (
          <div key={c.id} style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
              <Badge tone="info" appearance="subtle" size="sm">
                {c.dns_names.join(', ')}
              </Badge>
              <Badge tone="warning" appearance="subtle" size="sm">
                {c.status === 'awaiting_dns' ? 'Awaiting DNS' : c.status}
              </Badge>
            </div>
            {c.delegation && (
              <div style={{ display: 'grid', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 2 }}>
                  <Badge tone="primary" appearance="solid" size="sm">CNAME</Badge>
                  <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                    Add this record — not TXT — at your DNS provider
                  </span>
                </div>
                <PendingCnameRow label="Host / Name" value={c.delegation.cname_host} />
                <PendingCnameRow label="Value / Target" value={c.delegation.cname_target} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                startIcon={<RefreshCw size={15} />}
                disabled={recheck.isPending}
                onClick={() => recheck.mutate(c.id)}
              >
                {recheck.isPending ? 'Checking…' : 'Recheck'}
              </Button>
            </div>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

/** A copyable label/value line for a delegation CNAME record. */
function PendingCnameRow({ label, value }: { label: string; value: string }) {
  const { show } = useToast();
  const copy = () => {
    void navigator.clipboard.writeText(value);
    show({ tone: 'success', title: 'Copied' });
  };
  return (
    <div className={styles.cnameRow}>
      <span style={{ color: 'var(--vn-fg-muted)' }}>{label}</span>
      <code
        style={{
          fontFamily: 'var(--vn-font-mono, monospace)',
          wordBreak: 'break-all',
          background: 'var(--vn-bg-subtle)',
          padding: '2px 6px',
          borderRadius: 'var(--vn-radius-sm)',
        }}
      >
        {value}
      </code>
      <IconButton variant="ghost" size="sm" aria-label={`Copy ${label}`} onClick={copy}>
        <Copy size={13} />
      </IconButton>
    </div>
  );
}
